import { Field } from 'api/tasks';
import { Checkbox } from 'components/atoms/checkbox';
import { DatePicker } from 'components/atoms/date-picker';
import { Input, InputNumber, TextArea } from 'components/atoms/input';
import { Select } from 'components/atoms/select';
import {
  FieldType,
  filterableFieldType,
} from 'components/organisms/tasks/fields';
import { getBudgetedHoursConfigFromTemplate } from 'components/organisms/templates/projects/plan/commons';
import {
  BudgetedHoursCalculationMode,
  ProjectTemplateApi,
  ProjectTemplateTask,
} from 'data/templates/projects';
import { unreachable } from 'utils/unreachable';

export const getSelectType = (options: any[] = []) => {
  if (options.some((o) => 'imageUrl' in o)) {
    return 'user';
  }
  if (options.some((o) => o.color)) {
    return 'tag';
  }

  return undefined;
};

export const isMultiSelect = (field: Field) => {
  const type = filterableFieldType(field);

  return [FieldType.MultiUser, FieldType.MultiSelect].includes(type);
};

export const getField = (field: Field) => {
  const fieldType = filterableFieldType(field);

  switch (fieldType) {
    case FieldType.Text:
      return Input;
    case FieldType.MultiLineText:
      return TextArea;
    case FieldType.Number:
      return InputNumber;
    case FieldType.Boolean:
      return Checkbox;
    case FieldType.Date:
      return DatePicker;
    case FieldType.SingleSelect:
      return Select;
    case FieldType.MultiSelect:
      return Select;
    case FieldType.SingleUser:
      return Select;
    case FieldType.MultiUser:
      return Select;
    case FieldType.Assignee:
      return Select;
    case FieldType.Estimate:
      // estimate is not an actual custom field type and we do not have renderer for this. In future if it is needed, handle it accordingly
      return unreachable(fieldType as never);
    default:
      return unreachable(fieldType);
  }
};
const getEffortsFromTasks = (
  tasks: ProjectTemplateTask[],
  dynamicConditionSetOnParent: boolean,
) =>
  tasks.reduce(
    (total, task) => {
      const dynamiceConditionSet =
        dynamicConditionSetOnParent || task.filters != null;
      let effortFromSubTasks = {
        effortsForTasksWithoutDynamicConditions: 0,
        effortForAllTasks: 0,
      };

      if (task.subTasks.length > 0) {
        effortFromSubTasks = getEffortsFromTasks(
          task.subTasks,
          dynamiceConditionSet,
        );
      }

      return {
        effortsForTasksWithoutDynamicConditions:
          total.effortsForTasksWithoutDynamicConditions +
          (!dynamiceConditionSet ? task.effort ?? 0 : 0) +
          effortFromSubTasks.effortsForTasksWithoutDynamicConditions,
        effortForAllTasks:
          total.effortForAllTasks +
          (task.effort ?? 0) +
          effortFromSubTasks.effortForAllTasks,
      };
    },
    {
      effortsForTasksWithoutDynamicConditions: 0,
      effortForAllTasks: 0,
    },
  );

const getEffortsFromTemplate = (template: ProjectTemplateApi) =>
  template.templateContent.projectPhases.reduce(
    (totalEffort, projectPhase) => {
      const dynamicConditionSetOnParent = projectPhase.filters != null;
      const effortFromSubTasks = getEffortsFromTasks(
        projectPhase.tasks,
        dynamicConditionSetOnParent,
      );

      return {
        effortsForTasksWithoutDynamicConditions:
          totalEffort.effortsForTasksWithoutDynamicConditions +
          effortFromSubTasks.effortsForTasksWithoutDynamicConditions,
        effortForAllTasks:
          totalEffort.effortForAllTasks + effortFromSubTasks.effortForAllTasks,
      };
    },
    {
      effortsForTasksWithoutDynamicConditions: 0,
      effortForAllTasks: 0,
    },
  );

export const getBudgetedHoursFromTemplates = (
  templates: ProjectTemplateApi[],
): {
  minimumPossibleBudgetedHours: number;
  maximumPossibleBudgetedHours: number;
} => {
  const budgetedHoursFromTemplates = templates.reduce(
    (result, template) => {
      const bugdetedHoursConfig = getBudgetedHoursConfigFromTemplate(template);

      if (bugdetedHoursConfig) {
        if (bugdetedHoursConfig.mode === BudgetedHoursCalculationMode.Manual) {
          return {
            minimumPossibleBudgetedHoursInMinutes:
              result.minimumPossibleBudgetedHoursInMinutes +
              bugdetedHoursConfig.value * 60,
            maximumPossibleBudgetedHoursInMinutes:
              result.maximumPossibleBudgetedHoursInMinutes +
              bugdetedHoursConfig.value * 60,
          };
        }
        const efforts = getEffortsFromTemplate(template);

        return {
          minimumPossibleBudgetedHoursInMinutes:
            result.minimumPossibleBudgetedHoursInMinutes +
            efforts.effortsForTasksWithoutDynamicConditions,
          maximumPossibleBudgetedHoursInMinutes:
            result.maximumPossibleBudgetedHoursInMinutes +
            efforts.effortForAllTasks,
        };
      }

      return result;
    },
    {
      minimumPossibleBudgetedHoursInMinutes: 0,
      maximumPossibleBudgetedHoursInMinutes: 0,
    },
  );

  return {
    minimumPossibleBudgetedHours: Math.ceil(
      budgetedHoursFromTemplates.minimumPossibleBudgetedHoursInMinutes / 60,
    ),
    maximumPossibleBudgetedHours: Math.ceil(
      budgetedHoursFromTemplates.maximumPossibleBudgetedHoursInMinutes / 60,
    ),
  };
};
export const anyTemplateHasBudgetedHoursConfig = (
  templates: ProjectTemplateApi[],
): boolean =>
  templates.filter(
    ({ templateContent: { budgetedHours }, autoBudgetedHours }) =>
      autoBudgetedHours === true ||
      ((autoBudgetedHours === false || autoBudgetedHours == null) &&
        budgetedHours != null),
  ).length > 0;
